'use client';

import React, { useCallback, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import CustomInputField from 'src/components/shared/form-fields/custom-input-field';
import {
  Button,
  Form,
  FormFeedback,
  Modal,
  ModalBody,
  ModalHeader,
} from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import type { Resolver } from 'react-hook-form';
import * as Yup from 'yup';
import type { AxiosResponse } from 'axios';
import { sendAskQuestion } from 'src/services/jobs';
import catchHandler from 'src/helpers/catch-handler';
import ButtonWithLoader from 'src/components/shared/form-fields/loading-button';
import { ErrorMessage } from '@hookform/error-message';
import { updateVisibility } from 'src/redux/modals/slice';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';

interface AskQuestionFormData {
  question: string;
}

const AskQuestionModal = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState<string | false>(false);
  const dispatch = useAppDispatch();
  const { extraProps } = useAppSelector(
    (state: Record<string, unknown>) => (
      (state.modals as Record<string, unknown>)
        .askQuestionModal as Record<string, unknown>
    ),
  );
  const validationSchema = Yup.object().shape({
    question: Yup.string()
      .required('This is a required field')
      .matches(/^\s*\S[\s\S]*$/, 'This field cannot contain only whitespace'),
  });

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      question: '',
    },
    resolver: yupResolver(validationSchema) as unknown as Resolver<AskQuestionFormData>,
  });

  const handleModalClose = useCallback(() => {
    dispatch(
      updateVisibility({
        modalType: 'askQuestionModal',
        isVisible: false,
      }),
    );
  }, [dispatch]);

  const {
    setError,
    formState: { errors },
  } = methods;
  const onSubmit = (formData: AskQuestionFormData) => {
    setIsLoading(true);
    const ep = extraProps as Record<string, unknown> | undefined;
    const data = ep?.data as Record<string, unknown> | undefined;
    const job = data?.job as Record<string, unknown> | undefined;
    sendAskQuestion(job?.job_id as string, formData as unknown as Record<string, unknown>)
      ?.then((res: AxiosResponse) => {
        setIsLoading(false);
        const resData = res?.data as Record<string, unknown> | undefined;
        setShowSuccess((resData?.message as string) || 'Submitted successfully');
      })
      .catch((err: unknown) => {
        catchHandler(err, setError);
        setIsLoading(false);
      });
  };

  const ep = extraProps as Record<string, unknown> | undefined;
  const data = ep?.data as Record<string, unknown> | undefined;
  const job = data?.job as Record<string, unknown> | undefined;

  return (
    <Modal
      isOpen
      toggle={handleModalClose}
      centered
      className="global-modal ask-question-modal"
    >
      <ModalHeader toggle={handleModalClose}>
        Ask a Question regarding #
        {job?.job_no as string}
      </ModalHeader>
      <ModalBody>
        <div className="divider" />
        <FormProvider {...methods}>
          <Form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="ask-question-data"
          >
            {!showSuccess ? (
              <React.Fragment>
                <CustomInputField
                  type="textarea"
                  name="question"
                  placeholder="Write your question here"
                  className="custom-textarea"
                />
                {(errors as Record<string, unknown>)?.general_errors ? (
                  <ErrorMessage
                    errors={errors}
                    name="question"
                    render={({ message }: { message: string }) => (
                      <FormFeedback>
                        {message}
                      </FormFeedback>
                    )}
                  />
                ) : (
                  ''
                )}
                <div className="btn-action-data">
                  <Button color="secondary" onClick={handleModalClose}>
                    Close
                  </Button>
                  <ButtonWithLoader type="submit" color="primary" isLoading={isLoading}>
                    Submit
                  </ButtonWithLoader>
                </div>
              </React.Fragment>
            ) : (
              <p>{showSuccess}</p>
            )}
          </Form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

export default AskQuestionModal;
